/**
 * Public bounty detail + submit page, routed at "/bounties/:bountyId" (nested
 * in BrowseCollection) and "/account/pursuits/:bountyId" (UserHomepage).
 * Reads the bounty from location.state, renders it via BountyDisplay inside a
 * SubheadedPage, lets the user pin/unpin it as a pursuit (UserPursuitAPI +
 * UserContext helpers), and toggles an inline SubmissionCreate form driven
 * through a SubmissionCreateRef. Gates submitting behind email verification
 * and Stripe onboarding via AlertDialogs; errors surface via ErrorContext.
 */
import { Bounty } from "@/types/bounty";
import { useLocation, useNavigate } from "react-router-dom";
import SubmissionCreatePage from "@/pages/submission/SubmissionCreate";
import { SubheadedPage } from "@/components/SubheadedPage";
import { formatBountyCategorySexCategoryType } from "@/utils/bounty";
import { useUserState } from "@/contexts/UserContext";
import { useUserDispatch } from "@/contexts/UserContext";
import { useState, useRef, useEffect } from "react";
import { UserPursuitAPI } from "@/api/userPursuit";
import { removePursuitFromUser, addPursuitToUser } from "@/contexts/UserContext";
import { Pin, PinOff } from "lucide-react";
import BountyDisplay from "./BountyDisplay";
import { submitBountyText } from "@/utils/bounty";
import { ArrowUpFromLine } from "lucide-react";
import { SubmissionCreateRef } from "@/pages/submission/SubmissionCreate";
import AlertDialog from "@/components/AlertDialog";
import { BUTTON_SIZE } from "@/constants/ui";
import { useError } from "@/contexts/ErrorContext";

const BountyViewSubmit: React.FC = () => {
    const location = useLocation();
    const bounty = location.state.bounty as Bounty;
    const user = useUserState();
    const dispatch = useUserDispatch();
    const [showSubmissionCreate, setShowSubmissionCreate] = useState(false);
    const [loading, setLoading] = useState(false);
    const { showError } = useError();
    const pursuitAddedAlready = user?.pursuitIds.includes(bounty.id!);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const submissionCreateRef = useRef<SubmissionCreateRef>(null);
    const [isSubmissionValid, setIsSubmissionValid] = useState(false);
    const [showEmailDialog, setShowEmailDialog] = useState(false);
    const [showOnboardingDialog, setShowOnboardingDialog] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (bounty?.title) {
            document.title = bounty.title;
        }
        return () => {
            document.title = "porsui";
        };
    }, [bounty?.title]);

    async function handleAddRemovePursuitClick() {
        setLoading(true);
        try {
            if (pursuitAddedAlready) {
                await UserPursuitAPI.removePursuit(bounty.porsuiUserId!, bounty.id!);
                removePursuitFromUser(dispatch, user, bounty.id!);
            } else {
                await UserPursuitAPI.addPursuit(bounty.porsuiUserId!, bounty.id!);
                addPursuitToUser(dispatch, user, bounty.id!);
            }
        } catch (error) {
            const term = pursuitAddedAlready ? "remove" : "add";
            showError(`failed to ${term} pursuit`);
        } finally {
            setLoading(false);
        }
    }

    const handleSubmitButtonClick = () => {
        if (showSubmissionCreate) {
            submissionCreateRef.current?.handleSubmitClick();
        } else {
            if (!user?.email) {
                setShowEmailDialog(true);
            } else if (!user?.stripeOnboardingCompleted) {
                setShowOnboardingDialog(true);
            } else {
                setShowSubmissionCreate(true);
                setTimeout(() => {
                    if (buttonRef.current) {
                        // Get the header's height dynamically
                        const header = document.querySelector(".sticky") as HTMLElement;
                        const headerHeight = header ? header.offsetHeight : 0;

                        // Apply the calculated margin before scrolling
                        buttonRef.current.style.scrollMarginTop = `${headerHeight}px`;

                        buttonRef.current.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                        });
                    }
                }, 100);
            }
        }
    };

    const handleSubmissionCancel = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        setTimeout(() => {
            setShowSubmissionCreate(false);
        }, 100);
    };

    const handleSubmissionSuccess = () => {
        // Success is now handled by LoadingSpinner in SubmissionCreateEdit
        // Hide the form immediately when success callback is invoked
        setShowSubmissionCreate(false);
    };

    const submitButtonDisabled = () => {
        if (!user || bounty.porsuiUserId! === user?.id || loading) {
            return true;
        } else if (!showSubmissionCreate) {
            return false;
        } else if (!isSubmissionValid) {
            return true;
        }
        return false;
    };

    const handleValidationChange = (isValid: boolean) => {
        setIsSubmissionValid(isValid);
    };

    return (
        <SubheadedPage
            headerColor="bg-slate-50"
            heading={bounty.title}
            subheading={formatBountyCategorySexCategoryType(bounty)}
            headerAction={
                <div className="w-full relative">
                    <div className="absolute top-[-24px] right-0 flex flex-row space-x-4">
                        <button
                            className={`p-2 rounded-full hover:bg-slate-100 transition-colors ${
                                !user || bounty.porsuiUserId! === user?.id
                                    ? "text-slate-400 cursor-not-allowed"
                                    : "text-black"
                            }`}
                            onClick={handleAddRemovePursuitClick}
                            disabled={loading || !user || bounty.porsuiUserId! === user?.id}
                            title={
                                !user
                                    ? "sign in to add to pursuits"
                                    : pursuitAddedAlready
                                    ? "remove from pursuits"
                                    : "add to pursuits"
                            }
                        >
                            {pursuitAddedAlready ? (
                                <PinOff size={BUTTON_SIZE} />
                            ) : (
                                <Pin size={BUTTON_SIZE} />
                            )}
                        </button>
                    </div>
                </div>
            }
        >
            <div className="flex flex-col w-full">
                <BountyDisplay />
                <div className="flex flex-col items-center justify-center mb-4">
                    <button
                        ref={buttonRef}
                        className={`flex flex-col items-center justify-center my-2 ${
                            submitButtonDisabled()
                                ? "text-slate-400 cursor-not-allowed"
                                : "text-black"
                        }`}
                        onClick={handleSubmitButtonClick}
                        disabled={submitButtonDisabled()}
                    >
                        <ArrowUpFromLine size={BUTTON_SIZE} />
                        <p className="text-md font-porsui mt-2">{submitBountyText(bounty)}</p>
                    </button>
                </div>
                {showSubmissionCreate && (
                    <div className="w-full mb-4">
                        <SubmissionCreatePage
                            type="normal"
                            ref={submissionCreateRef}
                            bountyId={bounty.id!}
                            onCancel={handleSubmissionCancel}
                            onValidationChange={handleValidationChange}
                            onSuccess={handleSubmissionSuccess}
                        />
                    </div>
                )}
            </div>
            {showEmailDialog && (
                <AlertDialog
                    title="email required"
                    message="please enter your email in order to create a submission"
                    cancelText="not now"
                    confirmText="ok"
                    onConfirm={() => {
                        setShowEmailDialog(false);
                        navigate("/verifyEmail");
                    }}
                    onCancel={() => {
                        setShowEmailDialog(false);
                    }}
                />
            )}
            {showOnboardingDialog && (
                <AlertDialog
                    title="stripe configuration required"
                    message="please set up your stripe account to post a submission, so you can receive the reward"
                    cancelText="not now"
                    confirmText="ok"
                    onConfirm={() => {
                        setShowOnboardingDialog(false);
                        navigate("/account/payments");
                    }}
                    onCancel={() => {
                        setShowOnboardingDialog(false);
                    }}
                />
            )}
        </SubheadedPage>
    );
};

export default BountyViewSubmit;
